import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { ARTICLES, bySlug } from "@/data/articles";
import { AR_ARTICLE, AR_CATEGORY, arMap } from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Languages, Lightbulb } from "lucide-react";

const fmtDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "ar" ? "ar-QA" : "en-GB",
    { day: "2-digit", month: "long", year: "numeric" });

/** A single Learn article. Long-form, left-aligned, generous line height. */
export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const a = bySlug(slug);

  if (!a) {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-6" data-testid="article-missing">
        <div className="text-center">
          <h1 className="text-2xl font-bold">We haven't written that one yet</h1>
          <Button className="mt-5 rounded-full" onClick={() => navigate("/learn")}>Browse all articles</Button>
        </div>
      </div>
    );
  }

  const others = ARTICLES.filter((x) => x.slug !== a.slug).slice(0, 3);
  const title = (x) => (ar && AR_ARTICLE[x.slug]?.title) || x.title;
  const dek = (x) => (ar && AR_ARTICLE[x.slug]?.dek) || x.dek;
  const catLabel = (c) => (ar ? arMap(AR_CATEGORY, c) : c);

  return (
    <div className="min-h-screen bg-background" data-testid={`article-${a.slug}`}>
      <PublicHeader />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="mb-8 flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/learn")} data-testid="article-back">
            <ArrowLeft className="h-4 w-4 me-1.5" />{t("art_back")}
          </Button>
        </div>

        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#3E2E65]">{catLabel(a.category)}</span>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]" data-testid="article-title">
          {title(a)}
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">{dek(a)}</p>
        <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground border-b border-border pb-6">
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{a.read} {t("art_read")}</span>
          <span>{fmtDate(a.date, lang)}</span>
          <span>{t("art_research")}</span>
        </div>

        {ar && (
          <p className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-[11px] text-muted-foreground"
            data-testid="article-english-note">
            <Languages className="h-3.5 w-3.5" />{t("art_english_note")}
          </p>
        )}

        <article className="mt-10 space-y-10" dir="ltr" data-testid="article-body">
          {a.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-base md:text-lg font-semibold tracking-tight">{s.h}</h2>
              {(s.p || []).map((para, i) => (
                <p key={`${para}-${i}`} className="mt-3 text-[15px] leading-[1.75] text-foreground/80">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2.5">
                  {s.list.map((li, i) => (
                    <li key={`${li}-${i}`} className="flex gap-2.5 text-[15px] leading-relaxed text-foreground/80">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3E2E65] shrink-0" />{li}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>

        <div className="mt-12 rounded-3xl bg-[#F5F3FA] dark:bg-muted p-7" data-testid="article-takeaways">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Lightbulb className="h-4 w-4 text-[#3E2E65]" />{t("art_remember")}
          </div>
          <ul className="mt-4 space-y-2.5" dir="ltr">
            {a.takeaways.map((x, i) => (
              <li key={`${x}-${i}`} className="flex gap-2.5 text-sm leading-relaxed">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{x}
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("art_disclaimer")}</p>

        <div className="mt-14">
          <h2 className="text-base md:text-lg font-semibold">{t("art_keep_reading")}</h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            {others.map((o) => (
              <button key={o.slug} onClick={() => navigate(`/learn/${o.slug}`)}
                className="text-start rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-premium group"
                data-testid={`article-next-${o.slug}`}>
                <span className="text-[10px] uppercase tracking-[0.14em] text-[#3E2E65]">{catLabel(o.category)}</span>
                <h3 className="mt-2 text-sm font-semibold leading-snug">{title(o)}</h3>
                <span className="mt-3 flex items-center gap-1 text-[11px] text-[#3E2E65] group-hover:gap-2 transition-all">
                  {t("art_read_link")} <ArrowRight className="h-3 w-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
